<template>
  <section class="card">
    <button
      class="back-btn"
      type="button"
      title="返回首页入口管理"
      @click="goBackToTiles"
      @mouseenter="backBtnHover = true"
      @mouseleave="backBtnHover = false"
    >
      <img
        class="back-btn-icon"
        :src="backBtnHover ? backAboutActiveIcon : backAboutIcon"
        alt="返回"
      >
    </button>
    <div class="page-head">
      <h2>关于我们页管理</h2>
      <button class="btn btn-primary" :disabled="saving" @click="save">
        {{ saving ? '保存中...' : '保存' }}
      </button>
    </div>
    <p class="tip">
      说明：这里配置小程序「关于我们」三栏（导航栏+内容）。保存后 JSON 不变；库里的 <code>content_key</code> 可为
      <code>about</code> 或旧版 <code>about_tabs</code>，小程序会按实际 Key 请求接口。首页入口跳转须为
      <code>/pages/article/list?code=about</code>（保存本页时会自动纠正，勿用「我的」里的
      <code>/pages/mine/about</code>，那是另一页 <code>mine_about</code>）。
    </p>
    <p v-if="errorMsg" class="error">{{ errorMsg }}</p>
    <p v-if="okMsg" class="ok">{{ okMsg }}</p>

    <div class="panel tile-panel">
      <h3>首页入口（关于我们）</h3>
      <div class="tile-grid">
        <label class="field inline">
          <span>入口标题</span>
          <input v-model="tileForm.title" placeholder="例如：关于我们" />
        </label>
        <label class="field inline">
          <span>排序值（只读）</span>
          <input :value="tileForm.sortOrder || 0" disabled />
        </label>
        <div class="field inline tile-upload">
          <span>入口图片</span>
          <div class="img-row">
            <div class="upload-box upload-box--sm" @click="pickTileImage" @contextmenu.prevent="pickTileImage">
              <img v-if="tileForm.imageUrl" class="upload-img" :src="toFullUrl(tileForm.imageUrl)" />
              <div v-else class="upload-placeholder">
                <div class="upload-plus">+</div>
                <div class="upload-text">点击上传入口图</div>
              </div>
            </div>
            <div class="upload-hint">提示：点击图片可更改。</div>
          </div>
          <input ref="tileImageInput" class="hidden-input" type="file" accept="image/*" @change="onTileImageChange" />
        </div>
        <label class="field inline">
          <span>入口状态</span>
          <FancySelect v-model="tileForm.statusCode" :options="statusOptions" icon="●" />
        </label>
        <label class="field inline">
          <span>页面类型</span>
          <FancySelect
            v-model="tileForm.pageTypeId"
            :options="pageTypeOptions"
            icon="📄"
            placeholder="不标注类型"
            :searchable="true"
          />
        </label>
      </div>
    </div>

    <div class="topbar">
      <label class="field inline">
        <span>状态</span>
        <FancySelect v-model="statusCode" :options="statusOptions" icon="●" />
      </label>
      <label class="field inline">
        <span>页面标题</span>
        <input v-model="title" placeholder="例如：关于我们" />
      </label>
    </div>

    <div class="grid">
      <div class="panel">
        <h3>导航栏（3个）</h3>
        <div class="tab-row" v-for="(t, idx) in tabs" :key="t.key">
          <span class="idx">{{ idx + 1 }}</span>
          <input v-model="t.label" placeholder="例如：协会简介" />
        </div>
        <p class="subtip">提示：这里只改“导航栏名字”。每栏内容在右侧对应区域编辑。</p>
      </div>

      <div class="panel">
        <h3>第1栏：协会简介</h3>
        <label class="field">
          <span>简介正文</span>
          <textarea v-model="intro" placeholder="支持纯文本/简单HTML（小程序按富文本渲染）" />
        </label>
        <label class="field">
          <span>组织结构标题</span>
          <input v-model="orgStructureTitle" placeholder="例如：示意结构图" />
        </label>

        <div class="img-row">
          <div
            class="upload-box"
            @click="pickOrgChart"
            @contextmenu.prevent="pickOrgChart"
          >
            <img v-if="orgChartImage" class="upload-img" :src="toFullUrl(orgChartImage)" />
            <div v-else class="upload-placeholder">
              <div class="upload-plus">+</div>
              <div class="upload-text">点击上传组织结构图</div>
            </div>
          </div>
          <div class="upload-hint">
            提示：点击图片可更改。
          </div>
        </div>
        <input ref="orgChartInput" class="hidden-input" type="file" accept="image/*" @change="onOrgChartChange" />
      </div>

      <!-- 第2栏与第3栏并排占满一行，避免落在 360px 窄列导致长链接输入框撑破布局 -->
      <div class="bottom-two-cols">
        <div class="panel">
          <h3>第2栏：协会章程（列表）</h3>
          <div class="list-actions">
            <button type="button" class="btn btn-edit" @click="openCharterAdd">新增一条</button>
            <span class="subtip">在列表中查看已添加条目；点「新增一条」或「编辑」在弹窗里填写标题/日期/链接/封面。保存本页前请先点弹窗「确定」写入列表。</span>
          </div>

          <table v-if="charterItems.length" class="charter-table">
            <thead>
              <tr>
                <th class="col-idx">#</th>
                <th>标题</th>
                <th class="col-date">日期</th>
                <th class="col-url">链接</th>
                <th class="col-thumb">封面</th>
                <th class="col-actions">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(it, i) in charterItems" :key="it._k">
                <td>{{ i + 1 }}</td>
                <td class="td-title">{{ it.title || '—' }}</td>
                <td>{{ it.date || '—' }}</td>
                <td class="td-url" :title="it.wechatUrl || ''">{{ truncateMiddle(it.wechatUrl, 36) }}</td>
                <td class="td-thumb">
                  <img v-if="it.thumb" class="thumb-cell" :src="toFullUrl(it.thumb)" alt="" />
                  <span v-else class="muted">—</span>
                </td>
                <td class="td-actions">
                  <button type="button" class="btn btn-edit" @click="openCharterEdit(i)">编辑</button>
                  <button type="button" class="btn btn-delete" @click="removeCharter(i)">删除</button>
                </td>
              </tr>
            </tbody>
          </table>
          <p v-else class="charter-empty muted">暂无章程条目，请点击「新增一条」添加。</p>

          <div v-if="charterModalOpen" class="mask" @click.self="closeCharterModal">
            <div class="dialog dialog--shell charter-dialog" @click.stop>
              <h4>{{ charterModalIndex === null ? '新增章程条目' : '编辑章程条目' }}</h4>
              <div class="item-grid">
                <label class="field">
                  <span>标题</span>
                  <input
                    v-model="charterModalDraft.title"
                    placeholder="例如：协会章程（2026版）"
                    @input="charterModalDraft.resolveError = ''"
                  />
                </label>
                <label class="field">
                  <span>日期</span>
                  <input v-model="charterModalDraft.date" type="date" />
                </label>
                <label class="field full">
                  <span>公众号/网页链接</span>
                  <div class="url-row">
                    <input
                      v-model="charterModalDraft.wechatUrl"
                      type="url"
                      class="input-url"
                      placeholder="https://mp.weixin.qq.com/..."
                      autocomplete="off"
                      spellcheck="false"
                      @input="charterModalDraft.resolveError = ''"
                    />
                    <button
                      type="button"
                      class="btn btn-edit"
                      :disabled="resolvingCharterModal"
                      @click="resolveCharterModalTitle"
                    >
                      {{ resolvingCharterModal ? '识别中...' : '识别标题' }}
                    </button>
                  </div>
                  <small v-if="charterModalDraft.resolveError" class="field-error">{{ charterModalDraft.resolveError }}</small>
                </label>

                <div class="field full">
                  <span>封面图</span>
                  <div class="img-row">
                    <div
                      class="upload-box upload-box--sm"
                      @click="pickCharterModalThumb"
                      @contextmenu.prevent="pickCharterModalThumb"
                    >
                      <img v-if="charterModalDraft.thumb" class="upload-img" :src="toFullUrl(charterModalDraft.thumb)" alt="" />
                      <div v-else class="upload-placeholder">
                        <div class="upload-plus">+</div>
                        <div class="upload-text">点击上传封面</div>
                      </div>
                    </div>
                    <div class="upload-hint">提示：点击图片可更改。</div>
                  </div>
                </div>
              </div>
              <input
                ref="charterModalThumbInput"
                class="hidden-input"
                type="file"
                accept="image/*"
                @change="onCharterModalThumbChange"
              />
              <div class="dialog-foot">
                <button type="button" class="btn btn-cancel" @click="closeCharterModal">取消</button>
                <button type="button" class="btn btn-primary" @click="confirmCharterModal">确定</button>
              </div>
            </div>
          </div>
        </div>

        <div class="panel">
          <h3>第3栏：协会荣誉</h3>
          <label class="field">
            <span>主标题</span>
            <input v-model="honorsMainTitle" placeholder="例如：协会荣誉" />
          </label>
          <label class="field">
            <span>小标题</span>
            <input v-model="honorsSectionTitle" placeholder="例如：部分荣誉（可选）" />
          </label>
          <label class="field">
            <span>荣誉条目（每行一条）</span>
            <textarea v-model="honorsLinesText" placeholder="例如：\n2025 年度优秀协会\n2026 重点项目立项\n..." />
          </label>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import FancySelect from '@/components/FancySelect.vue'
import {
  createPageContent,
  getPageContentById,
  getPageContents,
  getTilePageTypes,
  getTiles,
  resolveArticleTitleByUrl,
  toMediaUrl,
  updatePageContent,
  updateTile,
  uploadImage,
} from '@/api'
import { isAboutStructuredKey } from './pageContentKeys.js'
import backAboutIcon from '@/assets/icons/back-about.svg'
import backAboutActiveIcon from '@/assets/icons/back-about-active.svg'

function safeJsonParse(s, fallback) {
  try {
    return JSON.parse(s)
  } catch {
    return fallback
  }
}

function genKey() {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`
}

/** 三栏「关于我们」走 /api/mp/pages/about；须打开 article/list?code=about。/pages/mine/about 走的是 mine_about（另一套路由） */
const ABOUT_MP_LIST_TARGET = '/pages/article/list?code=about'

function isAboutMpListTarget(linkTarget) {
  const u = String(linkTarget || '').trim().toLowerCase()
  return u.includes('/pages/article/list') && /[?&]code=about\b/i.test(u)
}

/** 兼容历史错误：入口曾默认为 /pages/mine/about */
function normalizeAboutTileLinkTarget(prev) {
  const t = String(prev || '').trim()
  if (!t || t === '/pages/mine/about') return ABOUT_MP_LIST_TARGET
  if (isAboutMpListTarget(t)) return t
  return ABOUT_MP_LIST_TARGET
}

export default {
  name: 'AboutTabsManageView',
  components: { FancySelect },
  props: {
    /** 从「页面内容管理」按 id 进入时传入，可加载含软删记录 */
    pageRowId: { type: Number, default: null }
  },
  data() {
    return {
      errorMsg: '',
      okMsg: '',
      saving: false,
      rowId: null,
      title: '关于我们',
      statusCode: 'ENABLED',
      statusOptions: [
        { value: 'ENABLED', label: '启用' },
        { value: 'DISABLED', label: '停用' }
      ],
      tabs: [
        { key: 'intro', label: '协会简介' },
        { key: 'charter', label: '协会章程' },
        { key: 'honors', label: '协会荣誉' }
      ],
      intro: '',
      orgStructureTitle: '示意结构图',
      orgChartImage: '',
      charterItems: [],
      honorsMainTitle: '协会荣誉',
      honorsSectionTitle: '',
      honorsLinesText: '',
      /** 协会章程：弹窗编辑单条 */
      charterModalOpen: false,
      charterModalIndex: null,
      charterModalDraft: {
        title: '',
        date: '',
        wechatUrl: '',
        thumb: '',
        resolveError: ''
      },
      resolvingCharterModal: false,
      /** 与库 content_key 一致，新建默认 about */
      persistedContentKey: 'about',
      tileId: null,
      backBtnHover: false,
      backAboutIcon,
      backAboutActiveIcon,
      tileRaw: null,
      pageTypeOptions: [{ value: '', label: '（不标注类型）' }],
      tileForm: {
        title: '关于我们',
        imageUrl: '',
        statusCode: 'ENABLED',
        pageTypeId: '',
        sortOrder: 0,
      },
    }
  },
  mounted() {
    this.loadPageTypeOptions()
    this.load()
  },
  methods: {
    async loadPageTypeOptions() {
      try {
        const rows = (await getTilePageTypes({ status: 'ENABLED' })) || []
        this.pageTypeOptions = [
          { value: '', label: '（不标注类型）' },
          ...rows.map((x) => ({ value: String(x.id), label: String(x.pageName || '') })).filter((x) => x.label)
        ]
      } catch {
        this.pageTypeOptions = [{ value: '', label: '（不标注类型）' }]
      }
    },
    goBackToTiles() {
      this.$router.push('/cms/home/tile')
    },
    async loadAboutTile() {
      const rows = (await getTiles()) || []
      const queryTileId = Number(this.$route?.query?.tileId || 0)
      let tile = null
      if (queryTileId > 0) {
        tile = rows.find((x) => Number(x.id) === queryTileId) || null
      }
      if (!tile) {
        tile =
          rows.find((x) => {
            const target = String(x?.linkTarget || '').trim().toLowerCase()
            const title = String(x?.title || '').trim()
            return (
              target === '/pages/mine/about' ||
              isAboutMpListTarget(x?.linkTarget) ||
              title === '关于我们'
            )
          }) || null
      }
      if (!tile) {
        this.errorMsg = '未找到“关于我们”首页入口，请先在首页入口管理中创建。'
        return
      }
      this.tileId = Number(tile.id)
      this.tileRaw = { ...tile }
      this.tileForm = {
        title: String(tile.title || '关于我们'),
        imageUrl: String(tile.imageUrl || ''),
        statusCode: String(tile.statusCode || 'ENABLED'),
        pageTypeId: tile.pageTypeId != null && tile.pageTypeId !== '' ? String(tile.pageTypeId) : '',
        sortOrder: Number(tile.sortOrder || 0),
      }
    },
    async load() {
      this.errorMsg = ''
      this.okMsg = ''
      try {
        await this.loadAboutTile()
        let row = null
        const pid = this.pageRowId != null ? Number(this.pageRowId) : NaN
        if (!Number.isNaN(pid) && pid > 0) {
          row = await getPageContentById(pid)
          const ck = String(row && row.contentKey ? row.contentKey : '').trim()
          if (row && !isAboutStructuredKey(ck)) {
            this.errorMsg = '该记录不是关于我们类型（content_key 应为 about 或 about_tabs）'
            return
          }
        } else {
          const list = (await getPageContents()) || []
          row = list.find(
            (it) =>
              isAboutStructuredKey(it.contentKey) &&
              (it.isDeleted === 0 || typeof it.isDeleted === 'undefined')
          )
        }
        if (!row) return

        this.persistedContentKey = String(row.contentKey || 'about').trim() || 'about'
        this.rowId = row.id
        this.title = row.title || '关于我们'
        this.statusCode = row.statusCode || 'ENABLED'

        const cfg = safeJsonParse(row.content || '', null)
        if (!cfg || cfg.variant !== 'aboutTabs') return

        if (Array.isArray(cfg.tabs) && cfg.tabs.length) this.tabs = cfg.tabs
        this.intro = cfg.intro || ''
        this.orgStructureTitle = cfg.orgStructureTitle || '示意结构图'
        this.orgChartImage = cfg.orgChartImage || ''
        this.charterItems = (Array.isArray(cfg.charterItems) ? cfg.charterItems : []).map((x) => ({ _k: genKey(), ...x }))
        const honors = cfg.honors || {}
        this.honorsMainTitle = honors.mainTitle || '协会荣誉'
        this.honorsSectionTitle = honors.sectionTitle || ''
        const lines = Array.isArray(honors.lines) ? honors.lines : []
        this.honorsLinesText = lines.join('\n')
      } catch (e) {
        this.errorMsg = e.message || '读取失败'
      }
    },

    buildPayload() {
      const honorsLines = (this.honorsLinesText || '')
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean)

      const cfg = {
        variant: 'aboutTabs',
        tabs: (this.tabs || []).map((t) => ({ key: t.key, label: (t.label || '').trim() })).filter((t) => t.key && t.label),
        intro: this.intro || '',
        orgStructureTitle: (this.orgStructureTitle || '').trim(),
        orgChartImage: this.orgChartImage || '',
        charterItems: (this.charterItems || []).map((it) => ({
          title: (it.title || '').trim(),
          date: (it.date || '').trim(),
          wechatUrl: (it.wechatUrl || '').trim(),
          thumb: it.thumb || ''
        })).filter((it) => it.title),
        honors: {
          mainTitle: (this.honorsMainTitle || '').trim(),
          sectionTitle: (this.honorsSectionTitle || '').trim(),
          lines: honorsLines
        }
      }
      return {
        contentKey: this.persistedContentKey || 'about',
        title: (this.title || '关于我们').trim(),
        content: JSON.stringify(cfg),
        statusCode: this.statusCode,
        remark: '关于我们三栏配置（JSON）'
      }
    },

    async save() {
      this.errorMsg = ''
      this.okMsg = ''
      this.saving = true
      try {
        if (!this.tileId) throw new Error('关于我们入口不存在，无法保存。')
        if (!String(this.tileForm.title || '').trim()) throw new Error('请填写入口标题')
        if (!String(this.tileForm.imageUrl || '').trim()) throw new Error('请上传入口图片')
        const raw = this.tileRaw || {}
        await updateTile(this.tileId, {
          ...raw,
          pageTypeId: this.tileForm.pageTypeId ? Number(this.tileForm.pageTypeId) : null,
          linkTarget: normalizeAboutTileLinkTarget(raw.linkTarget),
          title: String(this.tileForm.title || '').trim(),
          imageUrl: String(this.tileForm.imageUrl || '').trim(),
          statusCode: String(this.tileForm.statusCode || 'ENABLED'),
          sortOrder: Number(this.tileForm.sortOrder || 0),
        })
        const payload = this.buildPayload()
        if (this.rowId) {
          await updatePageContent(this.rowId, payload)
        } else {
          const created = await createPageContent(payload)
          this.rowId = created?.id || this.rowId
        }
        this.okMsg = '保存成功'
      } catch (e) {
        this.errorMsg = e.message || '保存失败'
      } finally {
        this.saving = false
      }
    },
    pickTileImage() {
      const input = this.$refs.tileImageInput
      if (input) input.click()
    },
    async onTileImageChange(e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      try {
        const data = await uploadImage(file)
        const url = data.url || ''
        if (!url) throw new Error('未获取到图片URL')
        this.tileForm.imageUrl = url
      } catch (err) {
        this.errorMsg = err.message || '上传失败'
      }
      e.target.value = ''
    },
    toFullUrl(url) {
      const u = String(url || '').trim()
      if (!u) return ''
      if (u.startsWith('data:')) return u
      return toMediaUrl(u)
    },

    pickOrgChart() {
      const input = this.$refs.orgChartInput
      if (input) input.click()
    },
    async onOrgChartChange(e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      try {
        const data = await uploadImage(file)
        const url = data.url || ''
        if (!url) throw new Error('未获取到图片URL')
        this.orgChartImage = url
      } catch (err) {
        this.errorMsg = err.message || '上传失败'
      }
      e.target.value = ''
    },
    truncateMiddle(str, max) {
      const s = String(str || '').trim()
      if (!s) return '—'
      const n = Number(max) || 36
      if (s.length <= n) return s
      const head = Math.max(6, Math.floor(n / 2) - 2)
      const tail = n - head - 3
      return s.slice(0, head) + '…' + s.slice(-Math.max(4, tail))
    },

    openCharterAdd() {
      this.errorMsg = ''
      this.charterModalIndex = null
      this.charterModalDraft = {
        title: '',
        date: '',
        wechatUrl: '',
        thumb: '',
        resolveError: ''
      }
      this.charterModalOpen = true
    },
    openCharterEdit(i) {
      if (i < 0 || i >= this.charterItems.length) return
      const it = this.charterItems[i]
      this.errorMsg = ''
      this.charterModalIndex = i
      this.charterModalDraft = {
        title: it.title || '',
        date: it.date || '',
        wechatUrl: it.wechatUrl || '',
        thumb: it.thumb || '',
        resolveError: ''
      }
      this.charterModalOpen = true
    },
    closeCharterModal() {
      this.charterModalOpen = false
      this.charterModalIndex = null
    },
    confirmCharterModal() {
      const title = String(this.charterModalDraft.title || '').trim()
      if (!title) {
        this.errorMsg = '请填写章程标题'
        return
      }
      this.errorMsg = ''
      const row = {
        _k:
          this.charterModalIndex === null
            ? genKey()
            : this.charterItems[this.charterModalIndex]._k,
        title,
        date: String(this.charterModalDraft.date || '').trim(),
        wechatUrl: String(this.charterModalDraft.wechatUrl || '').trim(),
        thumb: String(this.charterModalDraft.thumb || '').trim()
      }
      if (this.charterModalIndex === null) {
        this.charterItems = [...this.charterItems, row]
      } else {
        const arr = [...this.charterItems]
        arr[this.charterModalIndex] = row
        this.charterItems = arr
      }
      this.closeCharterModal()
    },

    async resolveCharterModalTitle() {
      const url = String(this.charterModalDraft.wechatUrl || '').trim()
      if (!url) {
        this.charterModalDraft = { ...this.charterModalDraft, resolveError: '请先填写公众号/网页链接' }
        return
      }
      try {
        this.errorMsg = ''
        this.resolvingCharterModal = true
        const data = await resolveArticleTitleByUrl(url)
        const title = String(data?.title || '').trim()
        if (!title) {
          this.charterModalDraft = {
            ...this.charterModalDraft,
            resolveError: '未识别到标题，请手动填写或粘贴“标题+链接”'
          }
          return
        }
        this.charterModalDraft = { ...this.charterModalDraft, title, resolveError: '' }
      } catch (e) {
        this.charterModalDraft = {
          ...this.charterModalDraft,
          resolveError: e.message || '识别标题失败'
        }
      } finally {
        this.resolvingCharterModal = false
      }
    },

    pickCharterModalThumb() {
      const input = this.$refs.charterModalThumbInput
      if (input) input.click()
    },
    async onCharterModalThumbChange(e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      try {
        const data = await uploadImage(file)
        const url = data.url || ''
        if (!url) throw new Error('未获取到图片URL')
        this.charterModalDraft = { ...this.charterModalDraft, thumb: url }
      } catch (err) {
        this.errorMsg = err.message || '上传失败'
      }
      e.target.value = ''
    },

    removeCharter(i) {
      const arr = [...this.charterItems]
      arr.splice(i, 1)
      this.charterItems = arr
    }
  }
}
</script>

<style scoped>
.card { background: #fff; border-radius: 12px; padding: 18px; }
.card * { box-sizing: border-box; }
.back-btn {
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  cursor: pointer;
  margin-bottom: 8px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform .15s ease;
}
.back-btn:hover {
  transform: translateX(-1px);
}
.back-btn-icon {
  width: 32px;
  height: 32px;
  display: block;
}
.page-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 4px;
}
.page-head h2 { margin: 0; }
.tip { background: #f5f8ff; border: 1px solid #dae5ff; color: #2a4b8d; padding: 10px 12px; border-radius: 8px; margin-bottom: 10px; line-height: 1.6; }
.error { color: #d92d20; margin: 8px 0; }
.ok { color: #067647; margin: 8px 0; }

.topbar { display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap; margin: 12px 0 16px; }
.grid { display: grid; grid-template-columns: minmax(0, 360px) minmax(0, 1fr); gap: 14px; align-items: start; }
@media (max-width: 1080px) { .grid { grid-template-columns: 1fr; } }

.bottom-two-cols {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 14px;
  align-items: start;
  min-width: 0;
}
@media (max-width: 1080px) {
  .bottom-two-cols { grid-template-columns: 1fr; }
}

.panel { border: 1px solid #eef2f7; border-radius: 12px; padding: 14px; background: #fff; min-width: 0; }
.panel h3 { margin: 0 0 12px; font-size: 14px; color: #101828; }
.tile-panel { margin-bottom: 12px; }
.tile-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.tile-upload { grid-column: span 2; }
.subtip { color: #98a2b3; font-size: 12px; margin-top: 8px; }

.field { display: flex; flex-direction: column; gap: 6px; font-size: 13px; color: #344054; margin-bottom: 10px; min-width: 0; }
.field.inline { min-width: 220px; margin-bottom: 0; }
.field > span { font-weight: 700; }
.field input, .field textarea {
  border: 1px solid #d0d7e2;
  border-radius: 10px;
  padding: 9px 10px;
  font-size: 13px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}
.field textarea { min-height: 110px; resize: vertical; }
.full { width: 100%; min-width: 0; }
.input-url { overflow-x: auto; }
.url-row { display: flex; align-items: center; gap: 8px; min-width: 0; }
.url-row .input-url { flex: 1; min-width: 0; }
.field-error { color: #d92d20; font-size: 12px; margin-top: 2px; }

.btn { border-radius: 10px; padding: 8px 12px; cursor: pointer; border: 1px solid transparent; font-size: 12px; }
.btn-primary { background: #2f6af6; color: #fff; }
.btn-edit { border-color: #bfc9db; background: #fff; color: #26457d; }
.btn-delete { border-color: #ffc8c2; background: #fff1f0; color: #d92d20; }
.btn:disabled { opacity: .6; cursor: not-allowed; }

.tab-row { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; min-width: 0; }
.idx { width: 20px; height: 20px; display: inline-flex; align-items: center; justify-content: center; border-radius: 8px; background: #eef4ff; color: #2f6af6; font-weight: 800; font-size: 12px; flex-shrink: 0; }
.tab-row input { flex: 1; min-width: 0; }

.img-row { display: flex; align-items: center; gap: 12px; min-width: 0; flex-wrap: wrap; }
.upload-box {
  width: 220px;
  height: 140px;
  border-radius: 12px;
  border: 1px dashed #cbd5e1;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
  transition: border-color .18s ease, background .18s ease, box-shadow .18s ease;
}
.upload-box:hover {
  border-color: #98b4ff;
  background: #f3f7ff;
  box-shadow: 0 8px 20px rgba(47, 106, 246, 0.12);
}
.upload-box--sm { width: 180px; height: 110px; }
.upload-img { width: 100%; height: 100%; object-fit: cover; }
.upload-placeholder {
  color: #98a2b3;
  font-size: 12px;
  padding: 8px 10px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.upload-plus {
  width: 26px;
  height: 26px;
  border-radius: 999px;
  border: 1px solid #c8d7ff;
  background: #fff;
  color: #2f6af6;
  font-size: 18px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.upload-text { color: #667085; font-size: 12px; }
.upload-hint { color: #98a2b3; font-size: 12px; }
.hidden-input { display: none; }

.list-actions { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-bottom: 10px; }
.muted { color: #98a2b3; }
.charter-empty { margin: 12px 0; font-size: 13px; }
.charter-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  margin-top: 4px;
}
.charter-table th,
.charter-table td {
  border-bottom: 1px solid #eef2f7;
  padding: 8px 10px;
  text-align: left;
  vertical-align: middle;
}
.charter-table th {
  color: #667085;
  font-weight: 600;
  background: #f9fafb;
}
.charter-table .col-idx { width: 44px; }
.charter-table .col-date { width: 120px; }
.charter-table .col-url { max-width: 200px; }
.charter-table .col-thumb { width: 72px; }
.charter-table .col-actions { width: 140px; white-space: nowrap; }
.charter-table .td-title { font-weight: 600; color: #101828; word-break: break-word; }
.charter-table .td-url {
  color: #475467;
  font-size: 12px;
  word-break: break-all;
  max-width: 220px;
}
.charter-table .td-actions .btn + .btn { margin-left: 6px; }
.thumb-cell {
  width: 56px;
  height: 40px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #eef2f7;
  vertical-align: middle;
}
.item-grid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 10px; min-width: 0; }
.item-grid .full { grid-column: span 2; min-width: 0; }

.dialog {
  width: 100%;
  max-width: 560px;
  background: #fff;
  border-radius: 12px;
  padding: 16px 18px;
  max-height: min(90vh, 720px);
  overflow: auto;
  box-shadow: 0 18px 60px rgba(15, 23, 42, 0.18);
}
.charter-dialog h4 {
  margin: 0 0 14px;
  font-size: 15px;
  color: #101828;
}
.dialog-actions {
  margin-top: 14px;
  text-align: right;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.btn-cancel {
  border-color: #d0d7e2;
  background: #fff;
  color: #344054;
}
</style>

